import {SectionView} from './SectionView'
import {PIXI} from '@colonist/pixi'
import {SpriteView} from './SpriteView'
import {AnimatedSpriteView} from './AnimatedSpriteView'
import {ArrayUtils} from '../../../../shared/utils/ArrayUtils'

export class AdaptiveSpriteSectionView extends SectionView {
    private readonly initialX: number
    private readonly initialY: number
    private readonly initialW: number
    private readonly initialH: number

    private readonly spriteViews: (SpriteView | AnimatedSpriteView)[]
    private readonly isPrimaryAxisHorizontal: boolean

    constructor(x: number, y: number, w: number, h: number, container: PIXI.Container, isPrimaryAxisHorizontal: boolean) {
        const removePadding = true
        super(x, y, w, h, container, removePadding)

        this.initialX = x
        this.initialY = y
        this.initialW = w
        this.initialH = h

        this.spriteViews = []
        this.isPrimaryAxisHorizontal = isPrimaryAxisHorizontal
    }

    addSpriteView(newSprite: SpriteView | AnimatedSpriteView) {
        if(newSprite.parent != undefined) newSprite.parent.removeChild(newSprite)
        this.scaleSpriteToFit(newSprite)
        this.addChild(newSprite)
        this.spriteViews.unshift(newSprite)
        this.isPrimaryAxisHorizontal ? newSprite.centerYInParent() : newSprite.centerXInParent()
        this.spreadSprites()
    }

    private scaleSpriteToFit(newSprite: SpriteView | AnimatedSpriteView) {
        this.isPrimaryAxisHorizontal ? this.scaleSpriteToFitHeight(newSprite) : this.scaleSpriteToFitWidth(newSprite)
    }

    private scaleSpriteToFitHeight(newSprite: SpriteView | AnimatedSpriteView) {
        const maxHeight = this.initialH
        if(newSprite.height <= maxHeight) return

        const scaleCorrection = maxHeight / newSprite.height
        newSprite.height *= scaleCorrection
        newSprite.width *= scaleCorrection
    }

    private scaleSpriteToFitWidth(newSprite: SpriteView | AnimatedSpriteView) {
        const maxWidth = this.initialW
        if(newSprite.width <= maxWidth) return

        const scaleCorrection = maxWidth / newSprite.width
        newSprite.height *= scaleCorrection
        newSprite.width *= scaleCorrection
    }

    removeSpriteView(spriteView: SpriteView | AnimatedSpriteView) {
        if(spriteView.parent != undefined) spriteView.parent.removeChild(spriteView)
        ArrayUtils.removeElementFromArray(this.spriteViews, spriteView)
        this.spreadSprites()
    }

    private spreadSprites() {
        this.isPrimaryAxisHorizontal ? this.spreadSpritesAlongHorizontalAxis() : this.spreadSpritesAlongVerticalAxis()
    }

    spreadSpritesAlongHorizontalAxis() {
        const numberOfSprites = this.spriteViews.length
        if(numberOfSprites <= 0) return

        const numberOfPartitions = numberOfSprites + 1
        const spacingX = this.initialW / numberOfPartitions
        let x = spacingX
        for(const spriteView of this.spriteViews) {
            const spriteScalingFactor = spriteView.width > spacingX ? spacingX / spriteView.width : 1
            spriteView.width *= spriteScalingFactor
            spriteView.height *= spriteScalingFactor
            const centerSpriteCorrection = spriteView.width / 2
            spriteView.x = x - centerSpriteCorrection
            spriteView.centerYInParent()
            x += spacingX
        }
    }

    spreadSpritesAlongVerticalAxis() {
        const numberOfSprites = this.spriteViews.length
        if(numberOfSprites <= 0) return

        const numberOfPartitions = numberOfSprites
        const partitionSpacingY = this.initialH / numberOfPartitions

        const iconPadding = partitionSpacingY * 0.05
        const maxIconheight = partitionSpacingY - iconPadding

        const startingY = partitionSpacingY / 2
        let y = startingY
        for(const spriteView of this.spriteViews) {
            const spriteScalingFactor = spriteView.height > maxIconheight ? maxIconheight / spriteView.height : 1

            spriteView.width *= spriteScalingFactor
            spriteView.height *= spriteScalingFactor

            spriteView.y = y - spriteView.height / 2
            spriteView.centerXInParent()
            y += partitionSpacingY
        }
    }
}
